/**
 * The tabs and windows the user has arranged, and the one place they change.
 *
 * This is the backing view model for LPN-1's central requirement: the user creates
 * tabs and windows, and the arrangement follows their account. It obeys the four
 * rules from §05 of docs/kmp-sharing-architecture.html — one [StateFlow] of one
 * [State], a [run] the platform calls once, intents named for the event, and no
 * knowledge of any platform.
 *
 * ── The division of labour with the toolkit ──────────────────────────────────
 *
 * This model owns *what exists*: which tabs there are, what they are called, which
 * windows are in each, and which of them is focused. The toolkit owns *where things
 * sit* — each window's rectangle, z-order, maximized state and the tab's layout
 * preset — and persists that itself under `UiSettingKeys.LAYOUT_STATE` through the
 * web shell's `Persister`. Neither half knows the other's format, and the two are
 * joined only by window id.
 *
 * That split is why this file has no geometry in it at all, and why the ticket's
 * "the layout of the tabs and windows should be persisted" is satisfied by two
 * keys rather than one.
 *
 * @see Workspace
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.clientserver.UiSettingKeys

/**
 * Owns the user's tabs and windows.
 *
 * @param storage where the workspace is read from and written back to.
 * @param scope the coroutine scope the model persists on. Injected with a default
 *   so a test can supply its own and await it, rather than racing a scope it cannot
 *   see. `Dispatchers.Main` because every consumer is a UI, and a state flow that
 *   emits off the main thread is a per-platform problem this model should not
 *   create.
 */
class WorkspaceBackingViewModel(
    private val storage: StorageRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) {
    private val _stateFlow = MutableStateFlow(State())

    /** The complete, render-ready description of the user's window arrangement. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * Restore the workspace, and keep restoring it whenever the user changes.
     *
     * Called once by the platform and never returns. Collecting the session — rather
     * than reading it once — is what makes sign-in work without a reload the day it
     * becomes real: a new user id means a different person's tabs, and this is the
     * only place that has to know it.
     *
     * `distinctUntilChanged` on the id alone, not the whole [SessionState]: an
     * unrelated field changing on the same account (a renamed display name) must not
     * throw away the tabs the user has open and reload them from the server.
     *
     * **Restoring is not destructive**, which is what makes the second arm safe. [restore]
     * assigns a whole [State], and a `StateFlow` drops an assignment equal to what it already
     * holds — so a settings revision produced by a *layout* change, or by a foreground refetch
     * that found nothing new, costs one decode and emits nothing. Only a workspace that
     * genuinely differs reaches a renderer.
     *
     * The one exception is the seed, which [restore] writes when the server has answered and
     * has nothing — and which does not come back here, because `putSetting` deliberately does
     * not bump the revision. It is self-limiting for the plainer reason too: the write puts the
     * seed in the cache, so the next restore finds a workspace and writes nothing.
     *
     * This client's own writes never get here: `commit` writes through
     * [StorageRepository.putSetting], which deliberately does **not** bump the revision, and
     * `ServerEventStream` drops the echo of the write by origin before the repository hears
     * about it at all. Two independent reasons, and both are wanted — the first covers a write
     * made while disconnected.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run() {
        combine(
            storage.sessionFlow.map { it?.user?.id }.distinctUntilChanged(),
            // LPN-27, and LPN-21's foreground refetch, arriving here. The cache has been
            // replaced with what the server now holds; this is what makes the tabs on screen
            // follow. Without it a workspace edited on the laptop would sit in the phone's
            // cache, correct and unrendered, until the app was restarted.
            storage.settingsRevisionFlow,
        ) { _, _ -> Unit }
            .collectLatest { restore() }
    }

    /**
     * Load the stored workspace, or seed the default one — and store the seed.
     *
     * The seeded default is deliberately not empty. A workspace with no tabs would
     * show the toolkit's empty-tab placeholder on a first visit, and a first visit is
     * exactly when the app has to demonstrate what it is — so a new user arrives with
     * one tab holding one window, which is the smallest arrangement that shows both
     * halves of the model working.
     *
     * ── Why the seed is written back ─────────────────────────────────────────
     *
     * It used to be pointedly *not* written, on the grounds that a workspace nobody
     * created should not be posted on every visitor's first page load. That argument
     * was made when this model was the only client of the key. It is not any more:
     * the phones read the same blob through
     * `WorkspaceOverviewBackingViewModel`, they have no default of their own, and
     * they are right not to invent one — a phone that seeded tabs would be writing
     * an arrangement from the device that cannot show it properly.
     *
     * So an unwritten seed does not mean "this account has arranged nothing". It
     * means the browser is showing tabs that exist nowhere else, and a phone that
     * syncs to the same account says *Nothing arranged yet* while the user is
     * looking at the arrangement in another window. Storing it is what makes the two
     * clients describe one account.
     *
     * ── The two nulls this must not confuse ──────────────────────────────────
     *
     * Both guards below are load-bearing, and both are protecting a real workspace
     * from being replaced by a default:
     *
     *  - **[StorageRepository.settingsWereRead]** separates "this account has stored
     *    no workspace" from "we could not ask". Only the first is permission to
     *    write. Without it, one failed settings fetch — a 500 on a foreground
     *    refetch, a sign-in whose second request did not land — would seed over
     *    everything the user has arranged.
     *  - **A blob that is present and undecodable is left alone**, which is why the
     *    raw string is tested rather than only the decoded workspace.
     *    [WorkspaceCodec.decode] collapses absent, corrupt and
     *    saved-by-a-newer-version into one null; the phone's model tells them apart
     *    for exactly this reason and refuses to write over the last two, and this is
     *    the same refusal.
     *
     * A seed that was written and did not reach the server is retried by the next
     * restore, since the cache is replaced from the server each time it is refetched
     * — see [StorageRepository.putSetting] for why a failed write is swallowed.
     */
    private suspend fun restore() {
        val blob = storage.setting(UiSettingKeys.WORKSPACE)
        val stored = WorkspaceCodec.decode(blob)
        if (stored != null) {
            _stateFlow.value = State(workspace = stored, isRestored = true)
            return
        }
        val seeded = defaultWorkspace()
        _stateFlow.value = State(workspace = seeded, isRestored = true)
        // Through `commit` rather than beside it, so there stays one funnel that writes this
        // key — and so the write cannot happen before `isRestored` is true, which the line
        // above has just made it.
        if (blob.isNullOrBlank() && storage.settingsWereRead()) commit(seeded)
    }

    // ── Tab intents ──────────────────────────────────────────────────────────

    /**
     * The user activated [tabId].
     *
     * Not suspending, like every intent here, and the reason is worth stating once:
     * the state is updated synchronously and the write to the server is launched on
     * this model's own scope. So the UI reflects the gesture on the same frame rather
     * than after a round-trip, Swift call sites stay free of completion handlers, and
     * a failed write costs persistence across a reload rather than the change itself
     * (see [StorageRepository.putSetting], which is where that failure is swallowed
     * and why).
     */
    fun onTabSelected(tabId: String) {
        val current = workspace()
        if (current.activeTabId == tabId || current.tabs.none { it.id == tabId }) return
        commit(current.copy(activeTabId = tabId))
    }

    /** The user asked for a new tab. It arrives empty, and active. */
    fun onTabAdded() {
        val current = workspace()
        val id = current.nextTabId()
        commit(
            current.copy(
                tabs = current.tabs + WorkspaceTab(id = id, name = DEFAULT_TAB_NAME),
                activeTabId = id,
            ),
        )
    }

    /**
     * The user closed [tabId].
     *
     * Closing the last tab seeds a fresh default one rather than leaving no tabs. The
     * alternative — refusing — leaves a close button that visibly does nothing, which
     * reads as broken rather than as forbidden; and an empty strip is a state the user
     * has no affordance to recover from except the "+" they may not think to look for.
     *
     * Focus moves to the neighbour on the right, falling back to the left, which is
     * what every tabbed interface does and therefore the only thing that will not
     * surprise anyone.
     */
    fun onTabClosed(tabId: String) {
        val current = workspace()
        val index = current.tabs.indexOfFirst { it.id == tabId }
        if (index < 0) return
        val remaining = current.tabs.filterNot { it.id == tabId }
        if (remaining.isEmpty()) {
            commit(defaultWorkspace())
            return
        }
        val nextActive = if (current.activeTabId == tabId) {
            remaining[index.coerceAtMost(remaining.lastIndex)].id
        } else {
            current.activeTabId
        }
        commit(current.copy(tabs = remaining, activeTabId = nextActive))
    }

    /**
     * The user renamed [tabId].
     *
     * A blank commit is discarded rather than stored: the toolkit treats an empty
     * rename as a cancel by default (see `AppShellSpec.allowEmptyPaneRename`, whose
     * tab equivalent this mirrors), and a nameless tab is not a state this model has
     * anything sensible to render.
     */
    fun onTabRenamed(tabId: String, name: String) {
        val trimmed = name.trim()
        if (trimmed.isEmpty()) return
        val current = workspace()
        if (current.tabs.none { it.id == tabId }) return
        commit(current.mapTab(tabId) { it.copy(name = trimmed) })
    }

    /**
     * The user dragged [sourceId] onto [targetId].
     *
     * @param before whether the source should land ahead of the target rather than
     *   after it — the toolkit reports which half of the target was dropped on.
     */
    fun onTabReordered(sourceId: String, targetId: String, before: Boolean) {
        if (sourceId == targetId) return
        val current = workspace()
        val source = current.tabs.firstOrNull { it.id == sourceId } ?: return
        val withoutSource = current.tabs.filterNot { it.id == sourceId }
        // Located AFTER the removal, so the index is one in the list being inserted
        // into. Computing it against the original list is the classic off-by-one here:
        // dragging left-to-right would land one place short of where it was dropped.
        val targetIndex = withoutSource.indexOfFirst { it.id == targetId }
        if (targetIndex < 0) return
        val insertAt = if (before) targetIndex else targetIndex + 1
        commit(current.copy(tabs = withoutSource.toMutableList().apply { add(insertAt, source) }))
    }

    // ── Window intents ───────────────────────────────────────────────────────

    /**
     * The user asked for a new window in [tabId].
     *
     * The new window becomes the tab's active one, so the thing that just appeared is
     * the thing that has focus.
     *
     * @param content what kind of window. Not defaulted, though it was while the list
     *   was the only kind: a default is a claim that one caller's answer is the obvious
     *   one, and with a gear that opens settings and (soon) a bookmark that opens an
     *   editor, `onWindowAdded(tabId)` would read as "add a window" while quietly meaning
     *   "add a list". Every call site names its kind instead, which costs a word and is
     *   why adding a kind is a case in [WindowContent] and a call here rather than a
     *   change to this function.
     * @param name what to call it, or null — the usual answer — for a window that takes
     *   its name from what it shows. See [WorkspaceWindow.name] on why the two are not the
     *   same thing, and [onWindowCloned] for the one caller that passes one through.
     */
    fun onWindowAdded(tabId: String, content: WindowContent, name: String? = null) {
        val current = workspace()
        if (current.tabs.none { it.id == tabId }) return
        val id = current.nextWindowId()
        commit(
            current.mapTab(tabId) { tab ->
                tab.copy(
                    windows = tab.windows + WorkspaceWindow(id = id, content = content, name = name),
                    activeWindowId = id,
                )
            },
        )
    }

    /**
     * *Clone window*: a second window exactly like [windowId], beside it and focused.
     *
     * The gesture that makes "a window is a saved filter" a thing somebody can do rather
     * than a thing the code believes. You narrow a list while browsing, decide the result
     * is worth keeping, and clone it — the filter carries over into a second window and
     * the one you were in stays yours to keep changing. It replaced a *Save as window*
     * button in the list body, which did the same thing in a place only lists could have
     * one and under a name that promised a kind of saving nothing here does.
     *
     * **Lists only.** Every other kind is a window there is one right number of: settings
     * holds the account's own, and two of it would be two editors of one truth racing to
     * write; an editor is *about* a bookmark, and a second one open on the same bookmark
     * in a different state is a merge conflict somebody has to lose. Only a list is a
     * view, and only a view is worth having two of. The web shell also declines to offer
     * the row for those kinds — see `paneOverflowMenu` in `main.kt` — so this guard is the
     * second of two, kept because the rule belongs to the model rather than to one host's
     * menu, and Android and iOS will ask the same question.
     *
     * **The name is copied verbatim**, which is what makes this a clone rather than a new
     * window that happens to share a filter. A window that was following its filter goes
     * on following it in both copies; one that had been renamed keeps that name, twice —
     * two rows reading *Triage* is momentarily odd and is also the truth, and renaming one
     * of them is the same gesture that named it in the first place.
     *
     * A no-op for an id no tab holds: cloning a window that has just been closed is an
     * ordinary race, not an error.
     */
    fun onWindowCloned(windowId: String) {
        val current = workspace()
        val tab = current.tabs.firstOrNull { it.window(windowId) != null } ?: return
        val window = tab.window(windowId) ?: return
        if (window.content !is WindowContent.BookmarkList) return
        onWindowAdded(tab.id, window.content, name = window.name)
    }

    /**
     * A list window's filter changed — a tag chip, a knob, a keystroke in the search box.
     *
     * The filter *is* the window, so it lives in the stored workspace rather than in the
     * window's view model: it survives a reload, it names the window in the sidebar, and
     * [onWindowCloned] copies it. This is the one intent that writes it.
     *
     * Fires as often as somebody types, and every call goes through [commit], which posts
     * to the server. **Hosts are expected to be lazy about calling it** — the web shell
     * waits until the typing pauses — because the alternative is a request per keystroke
     * to persist a search nobody has finished writing yet.
     *
     * A no-op for a window that is not a list, and for an id no tab holds: a keystroke
     * landing on a window that has just been closed is an ordinary race, not an error.
     */
    fun onWindowFilterChanged(windowId: String, filter: WindowContent.BookmarkList) {
        val current = workspace()
        val tab = current.tabs.firstOrNull { it.window(windowId) != null } ?: return
        val window = tab.window(windowId) ?: return
        if (window.content !is WindowContent.BookmarkList || window.content == filter) return
        commit(
            current.mapTab(tab.id) {
                it.copy(windows = it.windows.map { w -> if (w.id == windowId) w.copy(content = filter) else w })
            },
        )
    }

    /**
     * The user renamed a window through the toolkit's *Rename window* row.
     *
     * **A blank commit clears the name rather than being discarded** — the opposite of
     * [onTabRenamed], and the asymmetry is real rather than an oversight. A tab has no
     * derived name to fall back to, so a nameless tab is nothing this model can render; a
     * window has one, and emptying the field is how somebody asks for it back. The toolkit
     * only forwards the empty commit at all because the shell sets
     * `AppShellSpec.allowEmptyPaneRename`, which exists for hosts whose labels have a
     * meaningful "no custom name" state. Ours do.
     *
     * So the stored value is `null` for "never named, follow the filter" and a non-empty
     * string otherwise; the blank string never lands in [WorkspaceWindow.name].
     */
    fun onWindowRenamed(tabId: String, windowId: String, name: String) {
        val trimmed = name.trim().takeIf { it.isNotEmpty() }
        val current = workspace()
        val tab = current.tabs.firstOrNull { it.id == tabId } ?: return
        val window = tab.window(windowId) ?: return
        if (window.name == trimmed) return
        commit(
            current.mapTab(tabId) {
                it.copy(windows = it.windows.map { w -> if (w.id == windowId) w.copy(name = trimmed) else w })
            },
        )
    }

    /**
     * What [windowId] is called, or null when nobody has named it and it should say what
     * it shows instead.
     *
     * A query in the shape of [windowContent] beside it, and for the same caller: the
     * shell asks for a pane's label synchronously and has only an id to ask with. Null is
     * not "no name" — it is "derive one", and the deriving is
     * [filterSummary] for a list, a fixed word for settings, and the bookmark's own title
     * for an editor. Which of those it is belongs to the shell's `paneLabel`, because two
     * of the three are not knowable from here.
     */
    fun windowName(windowId: String): String? =
        workspace().tabs.firstNotNullOfOrNull { it.window(windowId) }?.name

    /**
     * The user pressed the settings gear.
     *
     * **One settings window, not two.** A second press focuses the one that exists,
     * wherever it is, rather than stacking another copy of a surface that has nothing to
     * distinguish two copies of. That makes the gear idempotent, which is what anybody
     * pressing it a second time is expecting — they are looking for the window they
     * already opened.
     *
     * Written here rather than in the web shell so Android and iOS get the same rule for
     * free when they grow a gear. How it is carried out — and why the lookup is by
     * content — is [onSingletonWindowActivated], which [onEditBookmarkActivated] shares.
     */
    fun onSettingsActivated() {
        onSingletonWindowActivated(WindowContent.Settings)
    }

    /**
     * The user asked to edit [bookmarkId] — a row's *Edit*, or the key that will replace
     * it.
     *
     * **One window per bookmark, and any number of bookmarks.** That is the whole
     * difference from the gear above, and [WindowContent.EditBookmark] being a `data
     * class` is what makes it one line rather than a special case: the same lookup by
     * content that finds *the* settings window finds *this bookmark's* editor, because
     * two `EditBookmark`s are equal exactly when they name the same row.
     *
     * Opening the same bookmark twice therefore focuses the editor that exists. Two views
     * of one row would be two forms over one truth, and the second Save would silently
     * undo whatever was typed into the first.
     */
    fun onEditBookmarkActivated(bookmarkId: Long) {
        onSingletonWindowActivated(WindowContent.EditBookmark(bookmarkId))
    }

    /**
     * Focus the window holding [content] if there is one, anywhere; otherwise open one in
     * the tab that is showing.
     *
     * Written as a lookup by *content* rather than by id, which is what an argument-less
     * kind leaves available (see [Workspace.nextWindowId] on why ids are allocated rather
     * than derived, and therefore why Lunicle's answer to this question — a pane id
     * derived from what the pane shows — is not open to us). The search is across every
     * tab, not just the showing one: two tabs each holding a settings window is exactly
     * the state this prevents, and it is reached by pressing the gear, switching tabs and
     * pressing it again.
     *
     * Reuses [onWindowSelected] for the focusing half — the intent that already knows a
     * window may be in a tab that is not showing. The alternative is a second path doing
     * the same two things, and the day one of them learns something the other does not is
     * the day the gear behaves differently from the sidebar.
     *
     * A new window lands in the tab that is showing. There is no other honest answer: the
     * gear is in the top bar and belongs to no tab, and a bookmark row belongs to the
     * window the user is looking at.
     *
     * Private, and it must stay private: it is only correct for a kind of which at most
     * one may be open per distinct content. [WindowContent.BookmarkList] is not such a
     * kind — "open two lists side by side" is the thing the workspace exists for — so it
     * goes through [onWindowAdded] instead.
     */
    private fun onSingletonWindowActivated(content: WindowContent) {
        val existing = windowShowing(content)
        if (existing != null) {
            onWindowSelected(existing.first, existing.second)
            return
        }
        onWindowAdded(workspace().activeTabId ?: return, content)
    }

    /**
     * The open settings window's id, or null when none is open.
     *
     * A query rather than a second intent, in the shape of [windowContent] beside it, and
     * it exists for one caller: a shell whose toolkit needs to be *told* to raise a window
     * it is already showing. Pushing a snapshot is not always enough — this model learns
     * about focus only from the intents it is sent, and the web toolkit reports an
     * ordinary click on a window's body to nobody, so after such a click the model and the
     * shell disagree about which window is focused and the gear's commit can be a no-op
     * that pushes nothing. The shell asks this and raises the window itself.
     *
     * Called straight after [onSettingsActivated] it answers about the window that intent
     * settled on, which is the only way it is meant to be used.
     */
    fun settingsWindowId(): String? = windowShowing(WindowContent.Settings)?.second

    /**
     * The id of the window editing [bookmarkId], or null when none is.
     *
     * [settingsWindowId]'s twin, for the same one caller and the same reason — an editor
     * reached a second time from a row has to be *raised*, not merely marked active in a
     * snapshot.
     */
    fun editBookmarkWindowId(bookmarkId: Long): String? =
        windowShowing(WindowContent.EditBookmark(bookmarkId))?.second

    /** The tab and window ids of the one window holding [content], or null. */
    private fun windowShowing(content: WindowContent): Pair<String, String>? =
        workspace().tabs.firstNotNullOfOrNull { tab ->
            tab.windows.firstOrNull { it.content == content }?.let { tab.id to it.id }
        }

    /**
     * What [windowId] contains, across every tab.
     *
     * The toolkit hands back a window id and nothing else when it asks an app to build
     * a body or a label — deliberately, since what a window *contains* is the app's
     * question and not the shell's. This is the answer, and it is a lookup rather than
     * a second map kept beside the workspace: one copy of the fact, in the state that
     * is already persisted and restored.
     *
     * Falls back to the list for an id this workspace does not know. Unreachable in
     * practice — the toolkit only asks about windows it was given — and the fallback is
     * a real window rather than an exception because a shell that threw while painting
     * would take the whole app with it over a window that cannot exist.
     */
    fun windowContent(windowId: String): WindowContent =
        workspace().tabs.firstNotNullOfOrNull { it.window(windowId) }?.content
            ?: WindowContent.BookmarkList()

    /**
     * The user closed a window.
     *
     * A tab with no windows left is kept, not closed. Closing it would mean the last
     * window's × silently destroys the tab, its name and its place in the strip —
     * three things the user did not ask to lose with one click on a fourth.
     */
    fun onWindowClosed(tabId: String, windowId: String) {
        val current = workspace()
        val tab = current.tabs.firstOrNull { it.id == tabId } ?: return
        if (tab.window(windowId) == null) return
        val remaining = tab.windows.filterNot { it.id == windowId }
        commit(
            current.mapTab(tabId) {
                it.copy(
                    windows = remaining,
                    activeWindowId = if (tab.activeWindowId == windowId) {
                        remaining.lastOrNull()?.id
                    } else {
                        tab.activeWindowId
                    },
                )
            },
        )
    }

    /**
     * The user moved a window to another tab, from the `⋮` menu's **Move to tab ▸**.
     *
     * The toolkit builds the destination list — every other tab in the snapshot, in strip
     * order — and reports which one was chosen; performing the move is ours, exactly as
     * closing is. Nothing about the window changes except which tab holds it: its id, its
     * content and its [WorkspaceWindow.name] all travel with it, because a rename and a
     * filter are statements about the window and the window is what moved.
     *
     * **It lands at the end of the target tab, and focused there**, which is where a
     * newly opened window lands — so arriving at that tab lands on the thing that was
     * just put in it. The source tab's focus moves to its last remaining window, the same
     * fallback [onWindowClosed] uses, and a source tab left with nothing keeps existing
     * for the reason it does there.
     *
     * **The showing tab does not follow.** *Move to tab* is how a working set is tidied —
     * this does not belong here, put it over there — and jumping the reader somewhere
     * they did not ask to look would make tidying cost a trip back. Lunicle and Lunamux
     * answer the same gesture the same way.
     *
     * No collision to worry about, unlike Lunicle: LunaPin allocates window ids from
     * [Workspace.nextWindowId] rather than deriving them from content, so an id names one
     * window in one tab and the same window cannot already be in the target.
     *
     * A pending filter write does not need flushing first (compare [onWindowCloned],
     * which does): the move carries the window rather than copying it, and
     * [onWindowFilterChanged] finds a window by id across every tab — so a debounced
     * filter still in flight lands on the window in its new home.
     *
     * Three silent refusals, all of them ordinary races or no-ops rather than errors: a
     * move to the tab it is already in, a move of a window that tab does not hold, and a
     * move onto a tab that no longer exists.
     *
     * @param tabId the tab the window is leaving. Named rather than searched for, because
     *   that is what the toolkit reports and it is the tab whose `⋮` was opened.
     * @param windowId the window that moved.
     * @param targetTabId where it lands.
     */
    fun onWindowMovedToTab(tabId: String, windowId: String, targetTabId: String) {
        if (tabId == targetTabId) return
        val current = workspace()
        val source = current.tabs.firstOrNull { it.id == tabId } ?: return
        val moved = source.window(windowId) ?: return
        if (current.tabs.none { it.id == targetTabId }) return
        val remaining = source.windows.filterNot { it.id == windowId }
        commit(
            current
                .mapTab(tabId) { tab ->
                    tab.copy(
                        windows = remaining,
                        activeWindowId = if (tab.activeWindowId == windowId) {
                            remaining.lastOrNull()?.id
                        } else {
                            tab.activeWindowId
                        },
                    )
                }
                .mapTab(targetTabId) { tab ->
                    tab.copy(windows = tab.windows + moved, activeWindowId = windowId)
                },
        )
    }

    /**
     * A window asked to close itself — the editor's *Cancel*, its `Esc`, and the close
     * that follows a delete.
     *
     * [onWindowClosed]'s one-argument twin, and it exists because the asker is the window
     * *body*, which knows its own id and has no business knowing which tab it is sitting
     * in. Finding the tab here rather than in the shell keeps that lookup in the one place
     * that holds the answer, the way [onWindowFocused] already does for a focus the
     * toolkit reports without a tab.
     *
     * A no-op for an id no tab holds, which is the honest response to a window that has
     * already gone: closing twice is a thing a keypress landing on a stale body can do.
     */
    fun onWindowCloseRequested(windowId: String) {
        val tabId = workspace().tabs.firstOrNull { it.window(windowId) != null }?.id ?: return
        onWindowClosed(tabId, windowId)
    }

    /**
     * The user picked a window in the sidebar tree — possibly one in a tab that is not
     * showing, which is the whole point of the tree.
     *
     * So this activates the tab as well. Activating only the window would leave the
     * user looking at the tab they were already on, having clicked a row that
     * appeared to do nothing.
     */
    fun onWindowSelected(tabId: String, windowId: String) {
        val current = workspace()
        val tab = current.tabs.firstOrNull { it.id == tabId } ?: return
        if (tab.window(windowId) == null) return
        commit(
            current.copy(activeTabId = tabId)
                .mapTab(tabId) { it.copy(activeWindowId = windowId) },
        )
    }

    /**
     * A window took focus by being clicked, or by keyboard navigation between windows.
     *
     * Distinct from [onWindowSelected] because the toolkit has *already* moved focus
     * itself by the time this arrives — this only records the fact so it survives a
     * reload. The tab is not changed: a focus event can only reach a window in the tab
     * that is showing.
     */
    fun onWindowFocused(windowId: String) {
        val current = workspace()
        val tabId = current.tabs.firstOrNull { tab -> tab.window(windowId) != null }?.id ?: return
        if (current.tabs.firstOrNull { it.id == tabId }?.activeWindowId == windowId) return
        commit(current.mapTab(tabId) { it.copy(activeWindowId = windowId) })
    }

    // ── Internals ────────────────────────────────────────────────────────────

    private fun workspace(): Workspace = _stateFlow.value.workspace

    /**
     * Publish [next] and write it back.
     *
     * The single funnel every intent goes through, which is what makes "the state and
     * the server agree" one claim to check rather than one per intent. Refuses to
     * write before the first restore: an intent arriving then would persist a
     * half-built workspace over the user's real one.
     */
    private fun commit(next: Workspace) {
        if (!_stateFlow.value.isRestored) return
        _stateFlow.value = _stateFlow.value.copy(workspace = next)
        val blob = WorkspaceCodec.encode(next)
        scope.launch { storage.putSetting(UiSettingKeys.WORKSPACE, blob) }
    }

    /** One tab, one window — see [restore] for why the default is not empty. */
    private fun defaultWorkspace(): Workspace {
        val tabId = "${Workspace.TAB_ID_PREFIX}1"
        val windowId = "${Workspace.WINDOW_ID_PREFIX}1"
        return Workspace(
            tabs = listOf(
                WorkspaceTab(
                    id = tabId,
                    name = DEFAULT_TAB_NAME,
                    windows = listOf(WorkspaceWindow(id = windowId)),
                    activeWindowId = windowId,
                ),
            ),
            activeTabId = tabId,
        )
    }

    /**
     * The whole of the arrangement, and whether it is real yet.
     *
     * @property workspace the tabs and their windows.
     * @property isRestored false until [run] has read the stored workspace. The web
     *   shell gates its first push on this, so the toolkit is never handed the empty
     *   workspace that exists for the first few milliseconds of a page load — which it
     *   would render as "no tabs" and then immediately replace.
     */
    data class State(
        val workspace: Workspace = Workspace(),
        val isRestored: Boolean = false,
    )

    private companion object {
        /**
         * What a tab is called before the user renames it.
         *
         * "My tab" rather than "Untitled", which is what it was: a tab is a working set
         * the user names, and the placeholder should read as one waiting for a name
         * rather than as a state of not having one. It is also deliberately not
         * something LunaPin-flavoured like "Bookmarks" — that would describe the
         * contents, and be a lie in the first tab somebody fills with something else.
         */
        const val DEFAULT_TAB_NAME = "My tab"
    }
}
